import { all, call, debounce, delay, fork, put, select, take, takeLatest } from 'redux-saga/effects';
import { first } from 'lodash';
import { extractPaymentDetailsData, formatCouponData, mapTransactionData } from '@containers/Payment/payment.helper';
import { selectCouponDetails, selectPaymentDetails } from '@containers/Payment/selectors';
import { selectQueryParams } from '@containers/Auth/selectors';
import { subscribeDriveOccurrenceGuestTimesRequest } from '@containers/Drive/slice';
import { paymentTransactionStatus } from '@containers/App/app.constant';
import fetchData from '@utils/fetchData';
import postData from '@utils/postData';
import { catchError } from '@utils/sentry';
import { RESCHEDULED_ORDER_ID, localStorageSetItem } from '@utils/localStorageHelpers';
import formatString from '@utils/stringHelper';
import paymentQueries from './queries';
import { createTransactionRequest, createTransactionSuccess, downloadTransactionReportFailure, downloadTransactionReportRequest, downloadTransactionReportSuccess, fetchOrderDetailsRequest, fetchOrderDetailsSuccess, fetchPaymentInvoiceRequest, fetchPaymentInvoiceSuccess, getTransactionRequest, getTransactionRequestSuccess, initiateTransactionRequest, initiateTransactionSuccess, payRequestTransactionFailed, payRequestTransactionRequest, payRequestTransactionSuccess, payUpdateTransactionStatusRequest, payUpdateTransactionStatusSuccess, paymentRescheduledSlotRequest, paymentRescheduledSlotSuccess, updateOrderStatusFailure, updateOrderStatusRequest, updateOrderStatusSuccess, validateCouponFailed, validateCouponRequest, validateCouponSuccess, } from './slice';
import { createApolloSubscriptionChannel } from '@utils/apolloEvent';
import { isPayTransactionStatus } from '@containers/Payment/validateSagaResponse';
export function* fetchOrderDetailsWorker(action) {
    const { orderId, callback } = action.payload;
    try {
        const queryParams = (yield select(selectQueryParams));
        const oId = orderId || Number(queryParams.order_id);
        const paymentOrderResponse = (yield call(fetchData, {
            queryString: paymentQueries.GET_ORDER_DETAILS_QUERY,
            queryVariables: { order_id: oId },
            queryKey: 'canx_get_order_details',
            forceRefresh: true,
        }));
        const orderDetails = paymentOrderResponse?.[0];
        if (orderDetails) {
            const formattedData = (yield call(extractPaymentDetailsData, orderDetails));
            const data = first(formattedData);
            yield put(fetchOrderDetailsSuccess(data));
            if (callback?.onSuccess) {
                yield call(callback.onSuccess, formattedData);
            }
        }
        else {
            throw new Error('An error occurred while fetching order details.');
        }
    }
    catch (error) {
        yield call(catchError, { title: 'Fetch Order Details Worker', error: error, skipToast: true });
        if (callback?.onError) {
            yield call(callback.onError, error);
        }
    }
}
export function* createTransactionWorker(action) {
    const { order_id, callback } = action.payload;
    try {
        const queryParams = (yield select(selectQueryParams));
        const couponDetails = (yield select(selectCouponDetails));
        const updatedQueryParams = queryParams.order_id && queryParams.order_id !== String(order_id)
            ? { ...queryParams, order_id: String(order_id) }
            : queryParams;
        const getOrderId = Number(updatedQueryParams.order_id || queryParams.order_id || order_id);
        if (getOrderId) {
            yield call(localStorageSetItem, RESCHEDULED_ORDER_ID, String(getOrderId));
        }
        const createTransactionResponse = (yield call(postData, {
            queryString: paymentQueries.CREATE_TRANSACTION_MUTATION,
            payload: {
                orderId: getOrderId,
                coupon_code: couponDetails.couponCode,
            },
            spreadPayload: true,
        }));
        const transactionData = createTransactionResponse?.data?.pay_create_transaction;
        if (transactionData) {
            yield put(createTransactionSuccess());
            if (callback?.onSuccess) {
                yield call(callback.onSuccess, transactionData);
            }
        }
        else {
            throw new Error('Transaction creation failed');
        }
    }
    catch (error) {
        yield call(catchError, { title: 'Create Transaction Worker', error: error, skipToast: true });
        if (callback?.onError) {
            yield call(callback.onError, error);
        }
    }
}
export function* validateCouponWorker(action) {
    const { order_id, coupon_code, callback } = action.payload;
    try {
        const response = (yield call(postData, {
            queryString: paymentQueries.VALIDATE_COUPON_MUTATION,
            payload: { orderId: order_id, couponCode: coupon_code },
            spreadPayload: true,
        }));
        const validateCouponResponse = response?.data?.pay_validate_coupon;
        if (validateCouponResponse) {
            const formatCouponRes = formatCouponData(validateCouponResponse);
            yield put(validateCouponSuccess(formatCouponRes));
            if (callback?.onSuccess) {
                yield call(callback.onSuccess, validateCouponResponse);
            }
        }
        else {
            throw new Error('Failed to validate coupon.');
        }
    }
    catch (error) {
        yield call(catchError, { title: 'Validate Coupon Worker', error: error, skipToast: true });
        yield put(validateCouponFailed());
        if (callback?.onError) {
            yield call(callback.onError, error);
        }
    }
}
export function* paymentRescheduledSlotWorker(action) {
    const { drive_occurrence_guest_id, callback } = action.payload;
    try {
        const response = (yield call(postData, {
            queryString: paymentQueries.PAYMENT_RESCHEDULED_SLOT,
            payload: { driveOccGuestId: drive_occurrence_guest_id },
            spreadPayload: true,
        }));
        const rescheduledApiResponse = response?.data?.canx_reschedule_drive_guest;
        if (rescheduledApiResponse) {
            yield put(paymentRescheduledSlotSuccess(rescheduledApiResponse));
            if (callback?.onSuccess) {
                yield call(callback.onSuccess, rescheduledApiResponse);
            }
        }
        else {
            throw new Error('Failed to validate rescheduled slot data.');
        }
    }
    catch (error) {
        yield call(catchError, { title: 'Payment Rescheduled Worker', error: error, skipToast: true });
        if (callback?.onError) {
            yield call(callback.onError, error);
        }
    }
}
export function* fetchPaymentInvoiceWorker(action) {
    const { invoiceData, callback } = action.payload;
    try {
        const paymentInvoiceResponse = yield call(fetchData, {
            queryString: paymentQueries.GET_PAYMENT_INVOICE,
            queryVariables: {
                invoice_number_check: invoiceData.invoiceNumber ? { _eq: invoiceData.invoiceNumber } : { _is_null: true },
            },
            queryKey: 'pay_invoice',
            forceRefresh: true,
        });
        const paymentInvoice = paymentInvoiceResponse?.[0];
        if (paymentInvoice) {
            yield put(fetchPaymentInvoiceSuccess(paymentInvoice));
            if (callback?.onSuccess) {
                yield call(callback.onSuccess, paymentInvoice);
            }
        }
        else {
            throw new Error('No invoice found.');
        }
    }
    catch (error) {
        yield call(catchError, { title: 'Fetch Payment Invoice Worker', error: error, skipToast: true });
        if (callback?.onError) {
            yield call(callback.onError, error);
        }
    }
}
export function* updateOrderStatusWorker(action) {
    const { order_id, state, slot_from, slot_to, coupon_code, callback } = action.payload;
    try {
        const queryParams = (yield select(selectQueryParams));
        const updatedQueryParams = queryParams.order_id && queryParams.order_id !== String(order_id)
            ? { ...queryParams, order_id: String(order_id) }
            : queryParams;
        const getOrderId = Number(updatedQueryParams.order_id || queryParams.order_id || order_id);
        // Store reschedule order ID to keep the latest order_id when it’s missing from localStorage
        if (getOrderId) {
            yield call(localStorageSetItem, RESCHEDULED_ORDER_ID, String(getOrderId));
        }
        const response = (yield call(postData, {
            queryString: paymentQueries.UPDATE_ORDER_STATUS_MUTATION,
            payload: {
                order_id: getOrderId,
                state,
                slot_from,
                slot_to,
                coupon_code: coupon_code,
            },
            spreadPayload: true,
        }));
        const updatedOrderStatus = response?.data?.cx_update_order_state;
        if (updatedOrderStatus) {
            yield put(updateOrderStatusSuccess());
            if (callback?.onSuccess) {
                yield call(callback.onSuccess, updatedOrderStatus);
            }
        }
        else {
            throw new Error('Failed to update order status.');
        }
    }
    catch (error) {
        yield call(catchError, { title: 'Update Order Status Worker', error: error, skipToast: true });
        yield put(updateOrderStatusFailure());
        if (callback?.onError) {
            yield call(callback.onError, error);
        }
    }
}
export function* initiateTransactionWorker(action) {
    const { order_id, callback } = action.payload;
    try {
        const queryParams = (yield select(selectQueryParams));
        const couponDetails = (yield select(selectCouponDetails));
        const getOrderId = Number(queryParams.order_id || order_id);
        const initiateTransactionResponse = (yield call(postData, {
            queryString: paymentQueries.INITIATE_TRANSACTION_MUTATION,
            payload: {
                orderId: getOrderId,
                coupon_code: couponDetails.couponCode,
            },
            spreadPayload: true,
        }));
        const transactionData = initiateTransactionResponse?.data?.pay_initiate_transaction;
        if (transactionData) {
            yield put(initiateTransactionSuccess());
            if (callback?.onSuccess) {
                yield call(callback.onSuccess, transactionData);
            }
        }
        else {
            throw new Error('Transaction initiation failed');
        }
    }
    catch (error) {
        yield call(catchError, { title: 'Initiate Transaction Worker', error: error, skipToast: true });
        if (callback?.onError) {
            yield call(callback.onError, error);
        }
    }
}
export function* payRequestTransactionWorker(action) {
    const { callback, order_id, gateway_type } = action.payload;
    try {
        const requestVariables = {
            order_id,
            gateway_type,
        };
        const payRequestTransactionResponse = (yield call(postData, {
            queryString: paymentQueries.PAY_REQUEST_TRANSACTION_MUTATION,
            payload: requestVariables,
            spreadPayload: true,
        }));
        const requestTransactionData = payRequestTransactionResponse?.data?.pay_request_transaction;
        if (requestTransactionData) {
            yield put(payRequestTransactionSuccess(requestTransactionData));
            if (callback?.onSuccess) {
                yield call(callback.onSuccess, requestTransactionData);
            }
        }
        else {
            throw new Error('Request transaction failed');
        }
    }
    catch (error) {
        yield call(catchError, { title: 'Pay Request Transaction Worker', error: error, skipToast: true });
        yield put(payRequestTransactionFailed());
        if (callback?.onError) {
            yield call(callback.onError, error);
        }
    }
}
export function* payUpdateTransactionStatusWorker(action) {
    const { paymentId, status, callback } = action.payload;
    try {
        const updateTransactionStatusResponse = (yield call(postData, {
            queryString: paymentQueries.PAY_UPDATE_TRANSACTION_STATUS_MUTATION,
            payload: {
                paymentId,
                status,
            },
            spreadPayload: true,
        }));
        const updateTransactionStatus = updateTransactionStatusResponse?.data?.pay_update_transaction;
        if (updateTransactionStatus) {
            yield put(payUpdateTransactionStatusSuccess(updateTransactionStatusResponse));
            if (callback?.onSuccess) {
                yield call(callback.onSuccess, updateTransactionStatus);
            }
        }
        else {
            throw new Error('Failed to update transaction status');
        }
    }
    catch (error) {
        yield call(catchError, { title: 'Pay Update Transaction Status Worker', error: error, skipToast: true });
        if (callback?.onError) {
            yield call(callback.onError, error);
        }
    }
}
export function* getTransactionStatusWorker(action) {
    const { orderId, callback } = action.payload;
    const query = paymentQueries.SUBSCRIBE_TRANSACTION_STATUS;
    const variables = { order_id: orderId };
    // Create the Apollo subscription channel to receive data and errors
    const channel = createApolloSubscriptionChannel({
        query,
        variables,
    });
    try {
        // Start an infinite loop to continuously listen for data from the subscription
        while (true) {
            // Use `take` to wait for the next event (either data or error) from the Apollo subscription channel.
            const { data, error } = (yield take(channel));
            if (error) {
                throw new Error(error.message);
            }
            if (isPayTransactionStatus(data)) {
                const transactionsCount = data?.pay_transaction?.length ?? 1;
                const extractTransactionDetails = mapTransactionData(data?.pay_transaction?.[transactionsCount - 1]);
                const isTransactionCompleted = formatString(extractTransactionDetails?.transactionStatus ?? '') ===
                    formatString(paymentTransactionStatus.COMPLETED);
                const isTransactionInitiated = formatString(extractTransactionDetails?.transactionStatus ?? '') ===
                    formatString(paymentTransactionStatus.INITIATED);
                if (isTransactionCompleted) {
                    yield delay(15000);
                }
                yield put(getTransactionRequestSuccess(extractTransactionDetails));
                if (isTransactionCompleted) {
                    const paymentDetails = (yield select(selectPaymentDetails));
                    const queryParams = (yield select(selectQueryParams));
                    const driveOccurrenceGuestId = paymentDetails?.drive_occurrence_guest_id || Number(queryParams?.drive_occurrence_guest_id);
                    if (driveOccurrenceGuestId) {
                        yield put(subscribeDriveOccurrenceGuestTimesRequest({ driveOccurrenceGuestId }));
                    }
                }
                if (callback?.onSuccess) {
                    yield call(callback.onSuccess, extractTransactionDetails);
                }
                if (!isTransactionInitiated) {
                    channel.close();
                    break;
                }
            }
        }
    }
    catch (error) {
        yield call(catchError, { title: 'Subscribe to pay order status', error: error, skipToast: true });
        if (callback?.onError) {
            yield call(callback.onError, error);
        }
    }
    finally {
        channel.close();
    }
}
export function* downloadTransactionReportWorker(action) {
    const { payload: { fileIds, callback }, } = action;
    try {
        if (!fileIds?.length) {
            throw new Error('No file ids provided for download.');
        }
        const responses = (yield all(fileIds.map((fileId) => call(fetchData, {
            queryString: paymentQueries.DOWNLOAD_TRANSACTION_REPORT,
            queryVariables: { file_id: fileId },
            queryKey: 'file_download_file',
            forceRefresh: true,
        }))));
        if (!responses.length) {
            throw new Error('Failed to retrieve download URLs.');
        }
        const payload = { files: responses };
        yield put(downloadTransactionReportSuccess());
        if (callback?.onSuccess) {
            yield call(callback.onSuccess, payload);
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'downloadTransactionReportWorker',
            error: error,
            skipToast: true,
        });
        yield put(downloadTransactionReportFailure());
        if (callback?.onError) {
            yield call(callback.onError, error);
        }
    }
}
export function* fetchOrderDetailsSagaWatcher() {
    yield debounce(1000, fetchOrderDetailsRequest.type, fetchOrderDetailsWorker);
}
export function* createTransactionSagaWatcher() {
    yield takeLatest(createTransactionRequest.type, createTransactionWorker);
}
export function* validateCouponSagaWatcher() {
    yield takeLatest(validateCouponRequest.type, validateCouponWorker);
}
export function* paymentRescheduledSlotSagaWatcher() {
    yield debounce(200, paymentRescheduledSlotRequest.type, paymentRescheduledSlotWorker);
}
export function* fetchPaymentInvoiceSagaWatcher() {
    yield debounce(1000, fetchPaymentInvoiceRequest.type, fetchPaymentInvoiceWorker);
}
export function* updateOrderStatusSagaWatcher() {
    yield takeLatest(updateOrderStatusRequest.type, updateOrderStatusWorker);
}
export function* initiateTransactionSagaWatcher() {
    yield takeLatest(initiateTransactionRequest.type, initiateTransactionWorker);
}
export function* payUpdateTransactionStatusSagaWatcher() {
    yield takeLatest(payUpdateTransactionStatusRequest.type, payUpdateTransactionStatusWorker);
}
export function* getTransactionStatusWatcher() {
    yield takeLatest(getTransactionRequest.type, getTransactionStatusWorker);
}
export function* payRequestTransactionSagaWatcher() {
    yield takeLatest(payRequestTransactionRequest.type, payRequestTransactionWorker);
}
export function* downloadTransactionReportWatcher() {
    yield takeLatest(downloadTransactionReportRequest.type, downloadTransactionReportWorker);
}
export function* fetchOrderDetailsRootSaga() {
    yield fork(fetchOrderDetailsSagaWatcher);
    yield fork(createTransactionSagaWatcher);
    yield fork(validateCouponSagaWatcher);
    yield fork(paymentRescheduledSlotSagaWatcher);
    yield fork(fetchPaymentInvoiceSagaWatcher);
    yield fork(updateOrderStatusSagaWatcher);
    yield fork(initiateTransactionSagaWatcher);
    yield fork(payUpdateTransactionStatusSagaWatcher);
    yield fork(getTransactionStatusWatcher);
    yield fork(payRequestTransactionSagaWatcher);
    yield fork(downloadTransactionReportWatcher);
}
